import React, { useState } from "react";
import { Btn, Badge, Field, Input, Select, Textarea, Modal, Toast, useToast } from "../components/ui";
import { isCompactViewport, T} from "../lib/globals";
import { createClientId } from "../lib/formatters";
import { makeCreateMeta, makeUpdateMeta } from "../lib/meta";
import { useSysLists } from "../sync/SyncProvider";

export function createSuppliersModule(deps) {
  const {
    useGlobalCollection,
    useSyncOnSave,
    PURCHASE_CATS,
    PROVIDER_SPECIALTIES,
  } = deps;

  function SuppliersModule({ session, isGestor }) {
  const SL = useSysLists();
  const [suppliers, setSuppliers] = useGlobalCollection("suppliers", []);
  const [providers, setProviders] = useGlobalCollection("providers", []);
  const [tab,       setTab]       = useState("suppliers");
  const [form,      setForm]      = useState(null);
  const [provForm,  setProvForm]  = useState(null);
  const [search,    setSearch]    = useState("");
  const [toast,     show]         = useToast();
  const syncOnSave = useSyncOnSave();
  const purchaseTypeOptions = React.useMemo(()=>{
    const source = (Array.isArray(SL?.CATALOG_PRODUCT_TYPES) && SL.CATALOG_PRODUCT_TYPES.length
      ? SL.CATALOG_PRODUCT_TYPES
      : Array.isArray(SL?.PURCHASE_CATS) && SL.PURCHASE_CATS.length
        ? SL.PURCHASE_CATS
        : PURCHASE_CATS
    );
    return source.map(v=>String(v||"").trim()).filter(Boolean);
  }, [SL?.CATALOG_PRODUCT_TYPES, SL?.PURCHASE_CATS]);
  const isAdmin = session?.role === "admin";
  const canEdit = isAdmin || isGestor;

  // ── SUPPLIERS ───────────────────────────────────────────────────
  const filteredSuppliers = suppliers
    .filter(s => !(s?._deleted || s?.deletedAt || s?.deleted_at))
    .filter(s =>
      !search || s.name?.toLowerCase().includes(search.toLowerCase()) ||
      s.category?.toLowerCase().includes(search.toLowerCase()) ||
      s.contact?.toLowerCase().includes(search.toLowerCase())
    );

  function saveSupplier() {
    if(!form.name?.trim()) { show("Nome obrigatório.", "error"); return; }
    const actorName = session?.name || "";
    if(form._new) {
      const clientId = String(form.clientId || createClientId("sup"));
      const newSup = {...form, id:String(form.id || clientId), clientId, _deleted:false, deletedAt:"", deleted_at:"", deletedBy:"", ...makeCreateMeta(actorName)};
      delete newSup._new;
      setSuppliers(prev => [...prev, newSup]);
    } else {
      setSuppliers(prev => prev.map(s => String(s.id)===String(form.id) ? ({ ...s, ...form, clientId:String(s.clientId || form.clientId || s.id || form.id || createClientId("sup")), _deleted:false, deletedAt:"", deleted_at:"", deletedBy:"", ...makeUpdateMeta(actorName, { deletedAt:"", deleted_at:"", deletedBy:"" }) }) : s));
    }
    show("✅ Fornecedor salvo!"); setForm(null); syncOnSave("suppliers");
  }

  function deleteSupplier(id) {
    if(!isAdmin) { show("Apenas admin pode excluir.", "error"); return; }
    const deletedAt = new Date().toISOString();
    setSuppliers(prev => prev.map(s => String(s.id) !== String(id) ? s : ({ ...s, _deleted:true, deletedAt, deleted_at:deletedAt, deletedBy:session?.name||"", ...makeUpdateMeta(session?.name || "", { deletedAt, deleted_at:deletedAt, deletedBy:session?.name || "" }) })));
    show("🗑️ Removido."); syncOnSave("suppliers");
  }

  // ── PROVIDERS ───────────────────────────────────────────────────
  const filteredProviders = providers
    .filter(p => !(p?._deleted || p?.deletedAt || p?.deleted_at))
    .filter(p =>
      !search || p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.specialty?.toLowerCase().includes(search.toLowerCase())
    );

  function saveProvider() {
    if(!provForm.name?.trim()) { show("Nome obrigatório.", "error"); return; }
    if(!provForm.specialty) { show("Selecione a especialidade.", "error"); return; }
    const actorName = session?.name || "";
    if(provForm._new) {
      const clientId = String(provForm.clientId || createClientId("prov"));
      const newProv = {...provForm, id:String(provForm.id || clientId), clientId, _deleted:false, deletedAt:"", deleted_at:"", deletedBy:"", ...makeCreateMeta(actorName)};
      delete newProv._new;
      setProviders(prev => [...prev, newProv]);
    } else {
      setProviders(prev => prev.map(p => String(p.id)===String(provForm.id) ? ({ ...p, ...provForm, clientId:String(p.clientId || provForm.clientId || p.id || provForm.id || createClientId("prov")), _deleted:false, deletedAt:"", deleted_at:"", deletedBy:"", ...makeUpdateMeta(actorName, { deletedAt:"", deleted_at:"", deletedBy:"" }) }) : p));
    }
    show("✅ Prestador salvo!"); setProvForm(null); syncOnSave("providers");
  }

  function deleteProvider(id) {
    if(!isAdmin) { show("Apenas admin pode excluir.", "error"); return; }
    const deletedAt = new Date().toISOString();
    setProviders(prev => prev.map(p => String(p.id) !== String(id) ? p : ({ ...p, _deleted:true, deletedAt, deleted_at:deletedAt, deletedBy:session?.name||"", ...makeUpdateMeta(session?.name || "", { deletedAt, deleted_at:deletedAt, deletedBy:session?.name || "" }) })));
    show("🗑️ Removido."); syncOnSave("providers");
  }

  const defSupplier = {_new:true, name:"", category:purchaseTypeOptions[0] || "", contact:"", email:"", cnpj:"", address:"", notes:""};
  const defProvider = {_new:true, name:"", specialty:"", contact:"", email:"", cpfCnpj:"", notes:"", active:true};

  return (
    <div>
      <Toast toast={toast}/>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <h2 style={{margin:0,color:T.text,fontSize:20,fontWeight:800}}>🏭 Cadastros Globais</h2>
          <div style={{fontSize:12,color:T.muted,marginTop:2}}>
            Fornecedores e prestadores compartilhados em todas as fazendas
          </div>
        </div>
        {canEdit&&<Btn onClick={()=>tab==="suppliers"?setForm(defSupplier):setProvForm(defProvider)}>
          + {tab==="suppliers"?"Novo Fornecedor":"Novo Prestador"}
        </Btn>}
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        <Btn variant={tab==="suppliers"?"primary":"ghost"} onClick={()=>{setTab("suppliers");setSearch("");}}>
          🏭 Fornecedores <span style={{marginLeft:4,background:T.accent+"33",color:T.accent,borderRadius:10,padding:"1px 7px",fontSize:11}}>{suppliers.length}</span>
        </Btn>
        <Btn variant={tab==="providers"?"primary":"ghost"} onClick={()=>{setTab("providers");setSearch("");}}>
          👷 Prestadores <span style={{marginLeft:4,background:"#0D948833",color:"#0D9488",borderRadius:10,padding:"1px 7px",fontSize:11}}>{providers.length}</span>
        </Btn>
      </div>

      {/* Search */}
      <Input value={search} onChange={e=>setSearch(e.target.value)}
        placeholder={`🔍 Buscar ${tab==="suppliers"?"fornecedor":"prestador"}...`}
        style={{marginBottom:14}}/>

      {/* ── SUPPLIERS TAB ── */}
      {tab==="suppliers"&&(
        <>
          {filteredSuppliers.length===0&&<div style={{color:T.muted,textAlign:"center",padding:48,fontSize:14}}>
            {search?"Nenhum fornecedor encontrado.":"Nenhum fornecedor cadastrado. Clique em + Novo Fornecedor."}
          </div>}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:12}}>
            {filteredSuppliers.map(s=>(
              <div key={s.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"16px 18px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div>
                    <div style={{fontWeight:700,color:T.text,fontSize:15}}>{s.name}</div>
                    <Badge color={T.accent} style={{marginTop:4}}>{s.category}</Badge>
                  </div>
                  {canEdit&&<div style={{display:"flex",gap:6}}>
                    <button onClick={()=>setForm({...s})} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:16}}>✏️</button>
                    {isAdmin&&<button onClick={()=>deleteSupplier(s.id)} style={{background:"none",border:"none",color:T.red,cursor:"pointer",fontSize:16}}>🗑️</button>}
                  </div>}
                </div>
                {s.contact&&<div style={{fontSize:12,color:T.muted,marginTop:4}}>📞 {s.contact}</div>}
                {s.email&&<div style={{fontSize:12,color:T.muted,marginTop:2}}>✉️ {s.email}</div>}
                {s.cnpj&&<div style={{fontSize:12,color:T.muted,marginTop:2}}>🪪 CNPJ: {s.cnpj}</div>}
                {s.address&&<div style={{fontSize:12,color:T.muted,marginTop:2}}>📍 {s.address}</div>}
                {s.notes&&<div style={{fontSize:11,color:T.muted,marginTop:6,fontStyle:"italic"}}>{s.notes}</div>}
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── PROVIDERS TAB ── */}
      {tab==="providers"&&(
        <>
          {filteredProviders.length===0&&<div style={{color:T.muted,textAlign:"center",padding:48,fontSize:14}}>
            {search?"Nenhum prestador encontrado.":"Nenhum prestador cadastrado. Clique em + Novo Prestador."}
          </div>}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:12}}>
            {filteredProviders.map(p=>(
              <div key={p.id} style={{background:T.card,border:`1px solid ${p.active!==false?T.border:T.muted+"44"}`,borderRadius:14,padding:"16px 18px",opacity:p.active!==false?1:.7}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div>
                    <div style={{fontWeight:700,color:T.text,fontSize:15}}>{p.name}</div>
                    <Badge color="#0D9488" style={{marginTop:4}}>{p.specialty}</Badge>
                    {p.active===false&&<Badge color={T.muted} style={{marginLeft:6}}>Inativo</Badge>}
                  </div>
                  {canEdit&&<div style={{display:"flex",gap:6}}>
                    <button onClick={()=>setProvForm({...p})} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:16}}>✏️</button>
                    {isAdmin&&<button onClick={()=>deleteProvider(p.id)} style={{background:"none",border:"none",color:T.red,cursor:"pointer",fontSize:16}}>🗑️</button>}
                  </div>}
                </div>
                {p.contact&&<div style={{fontSize:12,color:T.muted,marginTop:4}}>📞 {p.contact}</div>}
                {p.email&&<div style={{fontSize:12,color:T.muted,marginTop:2}}>✉️ {p.email}</div>}
                {p.cpfCnpj&&<div style={{fontSize:12,color:T.muted,marginTop:2}}>🪪 {p.cpfCnpj}</div>}
                {p.notes&&<div style={{fontSize:11,color:T.muted,marginTop:6,fontStyle:"italic"}}>{p.notes}</div>}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Supplier Form Modal */}
      <Modal open={!!form} onClose={()=>setForm(null)} title={form?(form._new?"Novo Fornecedor":"Editar Fornecedor"):""} wide>
        {form&&<div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
          <Field label="Nome *" col="1/-1"><Input value={form.name||""} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Razão social ou nome fantasia"/></Field>
          <Field label="Categoria">
            <Select value={form.category||""} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
              {purchaseTypeOptions.map(c=><option key={c}>{c}</option>)}
            </Select>
          </Field>
          <Field label="CNPJ / CPF"><Input value={form.cnpj||""} onChange={e=>setForm(f=>({...f,cnpj:e.target.value}))} placeholder="00.000.000/0000-00"/></Field>
          <Field label="Telefone / WhatsApp"><Input value={form.contact||""} onChange={e=>setForm(f=>({...f,contact:e.target.value}))} placeholder="(00) 00000-0000"/></Field>
          <Field label="E-mail"><Input value={form.email||""} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="email@fornecedor.com.br"/></Field>
          <Field label="Endereço" col="1/-1"><Input value={form.address||""} onChange={e=>setForm(f=>({...f,address:e.target.value}))} placeholder="Rua, número, cidade, estado"/></Field>
          <Field label="Observações" col="1/-1"><Textarea rows={2} value={form.notes||""} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Condições, prazos, observações..."/></Field>
          <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"space-between"}}>
            <div>{!form._new&&isAdmin&&<Btn variant="danger" onClick={()=>{deleteSupplier(form.id);setForm(null);}}>🗑️ Excluir</Btn>}</div>
            <div style={{display:"flex",gap:10}}>
              <Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn>
              <Btn onClick={saveSupplier}>Salvar</Btn>
            </div>
          </div>
        </div>}
      </Modal>

      {/* Provider Form Modal */}
      <Modal open={!!provForm} onClose={()=>setProvForm(null)} title={provForm?(provForm._new?"Novo Prestador":"Editar Prestador"):""} wide>
        {provForm&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <Field label="Nome *" col="1/-1"><Input value={provForm.name||""} onChange={e=>setProvForm(f=>({...f,name:e.target.value}))} placeholder="Nome completo ou razão social"/></Field>
          <Field label="Especialidade *">
            <Select value={provForm.specialty||""} onChange={e=>setProvForm(f=>({...f,specialty:e.target.value}))}>
              <option value="">Selecione...</option>
              {(SL.PROVIDER_SPECIALTIES||PROVIDER_SPECIALTIES).map(s=><option key={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="CPF / CNPJ"><Input value={provForm.cpfCnpj||""} onChange={e=>setProvForm(f=>({...f,cpfCnpj:e.target.value}))} placeholder="000.000.000-00"/></Field>
          <Field label="Telefone / WhatsApp"><Input value={provForm.contact||""} onChange={e=>setProvForm(f=>({...f,contact:e.target.value}))} placeholder="(00) 00000-0000"/></Field>
          <Field label="E-mail"><Input value={provForm.email||""} onChange={e=>setProvForm(f=>({...f,email:e.target.value}))}/></Field>
          <Field label="Status">
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginTop:8}}>
              <input type="checkbox" checked={provForm.active!==false} onChange={e=>setProvForm(f=>({...f,active:e.target.checked}))} style={{width:16,height:16,accentColor:T.green}}/>
              <span style={{fontSize:13,color:T.text}}>Ativo</span>
            </label>
          </Field>
          <Field label="Observações" col="1/-1"><Textarea rows={2} value={provForm.notes||""} onChange={e=>setProvForm(f=>({...f,notes:e.target.value}))}/></Field>
          <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"space-between"}}>
            <div>{!provForm._new&&isAdmin&&<Btn variant="danger" onClick={()=>{deleteProvider(provForm.id);setProvForm(null);}}>🗑️ Excluir</Btn>}</div>
            <div style={{display:"flex",gap:10}}>
              <Btn variant="ghost" onClick={()=>setProvForm(null)}>Cancelar</Btn>
              <Btn onClick={saveProvider}>Salvar</Btn>
            </div>
          </div>
        </div>}
      </Modal>
    </div>
  );
}



  return SuppliersModule;
}
